import { useState } from 'react'
import { Link } from 'react-router-dom'
import { Bell, Search, Sun, Moon, Menu, LogOut, User, Settings } from 'lucide-react'
import { useAuth } from '@/hooks/use-auth'
import { useTheme } from '@/providers/theme-provider'
import { useNotificationCount } from '@/hooks/api/use-notifications'
import { cn } from '@/lib/utils'

interface HubHeaderProps {
  onToggleSidebar: () => void
}

export function HubHeader({ onToggleSidebar }: HubHeaderProps) {
  const { user, logout } = useAuth()
  const { resolvedTheme, setTheme } = useTheme()
  const { data: notifData } = useNotificationCount()
  const [showUserMenu, setShowUserMenu] = useState(false)
  const [showNotifications, setShowNotifications] = useState(false)

  const unreadCount = notifData?.count ?? 0

  return (
    <header className="sticky top-0 z-40 h-14 border-b border-border bg-background/95 backdrop-blur supports-[backdrop-filter]:bg-background/60">
      <div className="flex h-full items-center gap-4 px-4">
        <button
          onClick={onToggleSidebar}
          className="inline-flex items-center justify-center rounded p-1.5 text-muted-foreground hover:bg-accent hover:text-foreground lg:hidden"
        >
          <Menu className="h-5 w-5" />
        </button>

        <Link to="/hub" className="flex items-center gap-2 font-heading text-lg font-semibold tracking-tight">
          New<span className="text-primary">BX</span>
        </Link>

        <div className="flex-1" />

        <div className="hidden md:flex items-center gap-1 rounded-lg border border-border bg-muted/50 px-3 py-1.5 text-sm text-muted-foreground">
          <Search className="h-3.5 w-3.5" />
          <span className="ml-1">Search...</span>
          <kbd className="ml-4 rounded bg-background px-1.5 py-0.5 text-[10px] font-mono border border-border">
            /
          </kbd>
        </div>

        <div className="flex items-center gap-1">
          <button
            onClick={() => setTheme(resolvedTheme === 'dark' ? 'light' : 'dark')}
            className="inline-flex items-center justify-center rounded p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
          >
            {resolvedTheme === 'dark' ? <Sun className="h-4 w-4" /> : <Moon className="h-4 w-4" />}
          </button>

          <div className="relative">
            <button
              onClick={() => setShowNotifications(!showNotifications)}
              className="relative inline-flex items-center justify-center rounded p-2 text-muted-foreground hover:bg-accent hover:text-foreground"
            >
              <Bell className="h-4 w-4" />
              {unreadCount > 0 && (
                <span className="absolute -top-0.5 -right-0.5 flex h-4 min-w-[16px] items-center justify-center rounded-full bg-primary px-1 text-[10px] font-medium text-primary-foreground">
                  {unreadCount > 99 ? '99+' : unreadCount}
                </span>
              )}
            </button>

            {showNotifications && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowNotifications(false)} />
                <div className="absolute right-0 top-full mt-2 z-50 w-80 rounded-lg border border-border bg-popover shadow-overlay animate-slide-down">
                  <div className="flex items-center justify-between border-b border-border px-4 py-3">
                    <h3 className="font-heading text-sm font-semibold">Notifications</h3>
                    <Link
                      to="/hub/notifications"
                      className="text-xs text-primary hover:underline"
                      onClick={() => setShowNotifications(false)}
                    >
                      View all
                    </Link>
                  </div>
                  <div className="max-h-80 overflow-y-auto p-2">
                    {unreadCount === 0 ? (
                      <p className="py-8 text-center text-sm text-muted-foreground">
                        No new notifications
                      </p>
                    ) : (
                      <p className="py-4 text-center text-sm text-muted-foreground">
                        {unreadCount} unread notification{unreadCount !== 1 ? 's' : ''}
                      </p>
                    )}
                  </div>
                </div>
              </>
            )}
          </div>

          <div className="relative ml-1">
            <button
              onClick={() => setShowUserMenu(!showUserMenu)}
              className="flex items-center gap-2 rounded-lg p-1.5 hover:bg-accent"
            >
              <div className="flex h-7 w-7 items-center justify-center rounded-full bg-primary text-xs font-medium text-primary-foreground">
                {(user?.display_name ?? user?.email)?.[0]?.toUpperCase() ?? '?'}
              </div>
            </button>

            {showUserMenu && (
              <>
                <div className="fixed inset-0 z-40" onClick={() => setShowUserMenu(false)} />
                <div className="absolute right-0 top-full mt-2 z-50 w-56 rounded-lg border border-border bg-popover shadow-overlay animate-slide-down">
                  <div className="border-b border-border px-4 py-3">
                    <p className="text-sm font-medium">{user?.display_name ?? 'User'}</p>
                    <p className="text-xs text-muted-foreground">{user?.email}</p>
                  </div>
                  <div className="p-1">
                    <Link
                      to="/hub/profiles/me"
                      className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-accent"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <User className="h-4 w-4 text-muted-foreground" />
                      Profile
                    </Link>
                    <Link
                      to="/hub/notification-settings"
                      className="flex items-center gap-2 rounded px-3 py-2 text-sm hover:bg-accent"
                      onClick={() => setShowUserMenu(false)}
                    >
                      <Settings className="h-4 w-4 text-muted-foreground" />
                      Settings
                    </Link>
                    <button
                      onClick={() => { setShowUserMenu(false); logout() }}
                      className="flex w-full items-center gap-2 rounded px-3 py-2 text-sm text-danger hover:bg-danger-muted"
                    >
                      <LogOut className="h-4 w-4" />
                      Sign out
                    </button>
                  </div>
                </div>
              </>
            )}
          </div>
        </div>
      </div>
    </header>
  )
}
